/**
 * SCHEDE ORIZZONTALI SUL TELEFONO (5 ott 2026 sera, Davide: "guarda come e' fatto su Nove25: ha un menu' che
 * scorre da sinistra a destra").
 *
 * Il modello Grasshopper descrive l'interfaccia con un "accordion" (le tendine INDIRIZZO, FORMA, MODELLO, DEDICA,
 * FINITURA). Sul computer va bene cosi'. Sul telefono, invece, le tendine una sotto l'altra sprecano spazio e non
 * sono intuitive: qui, PRIMA che l'App Builder disegni l'interfaccia, il JSON viene trasformato in SCHEDE (tabs):
 * una scheda per ogni gruppo di parametri, con dentro i parametri del gruppo senza piu' la tendina.
 * L'App Builder sa gia' disegnare le schede (campo "tabs" del contenitore): non si tocca il modello.
 * Vale solo per gli schermi stretti (< 600 px); il computer e il totem restano come sono.
 *
 * (9 ott 2026, Davide: "dal sito, da PC, mi da' le schede invece delle tendine") Nel negozio il plugin carica il
 * configuratore in una finestra NASCOSTA (larga 0 px = "telefono") e la mostra solo al clic su "Personalizza":
 * la scelta fatta una volta sola all'avvio restava "schede" anche sul computer. Ora la scelta si RIFA' quando la
 * larghezza passa la soglia (ascoltaSchermoTelefono, usato in useSessionWithAppBuilder e in EidosTotem): il JSON
 * originale non viene piu' modificato, le schede si costruiscono su una copia.
 */
import {
	IAppBuilder,
	IAppBuilderContainer,
	IAppBuilderExportRef,
	IAppBuilderParameterRef,
	IAppBuilderStandardContainer,
	IAppBuilderTab,
	IAppBuilderWidget,
	IAppBuilderWidgetPropsAccordion,
} from "@AppBuilderLib/features/appbuilder/config/appbuilder";

/** telefono: schermo piu' stretto di 600 px (stessa soglia di totem.css e di EidosTotem) */
export function schermoTelefono(): boolean {
	try {
		return typeof window !== "undefined" && window.matchMedia("(max-width: 599px)").matches;
	} catch {
		return false;
	}
}

/**
 * Chiama `cb(telefono)` ogni volta che la finestra passa da "telefono" a "computer" o viceversa (per esempio la
 * finestra nascosta del negozio che si apre). Restituisce la funzione che smette di ascoltare.
 */
export function ascoltaSchermoTelefono(cb: (telefono: boolean) => void): () => void {
	try {
		const mq = window.matchMedia("(max-width: 599px)");
		const cambia = () => cb(mq.matches);
		mq.addEventListener("change", cambia);
		return () => mq.removeEventListener("change", cambia);
	} catch {
		return () => {};
	}
}

/** copia profonda del JSON dell'App Builder: le schede si costruiscono sulla copia, l'originale resta a tendine */
function copia<T>(dati: T): T {
	try {
		return structuredClone(dati);
	} catch {
		return JSON.parse(JSON.stringify(dati)) as T;
	}
}

/** "INDIRIZZO E SEGNAPOSTO" -> "INDIRIZZO", "FINITURA E TAGLIA" -> "FINITURA": sulla barra stretta del telefono */
function nomeBreve(nome: string): string {
	const n = nome.trim();
	const i = n.toUpperCase().indexOf(" E ");
	return i > 3 ? n.slice(0, i) : n;
}

function eContenitoreStandard(c: IAppBuilderContainer): c is IAppBuilderStandardContainer {
	const nome = String((c as {name?: unknown}).name ?? "");
	return nome === "right" || nome === "bottom" || nome === "left" || nome === "top";
}

/**
 * (6 ott 2026) TOGLIE IL PULSANTE DI PROVA "PROVA: salva configurazione" (azione createModelState) dal riquadro
 * prezzo: Davide lo vuole via. Nel file Grasshopper e' gia' stato rimosso (AB_Salva, AB_AzioneSalva) dalla v33 in
 * poi; questo filtro serve finche' online c'e' ancora la v32. "Aggiungi al carrello" (addToCart) resta.
 */
export function togliAzioniDiProva(dati: IAppBuilder): void {
	type Controllo = {type?: string; props?: {definition?: {type?: string}; label?: string}};
	type Azione = {type?: string};
	for (const c of dati.containers ?? []) {
		const cont = c as {widgets?: IAppBuilderWidget[]};
		for (const w of cont.widgets ?? []) {
			if (w.type === "controls") {
				const props = w.props as {controls?: Controllo[]};
				if (props.controls) props.controls = props.controls.filter((k) => k.props?.definition?.type !== "createModelState");
			} else if (w.type === "actions") {
				const props = w.props as {actions?: Azione[]};
				if (props.actions) props.actions = props.actions.filter((k) => k.type !== "createModelState");
			}
		}
	}
}

/**
 * (24 ott 2026, Davide: «la sezione forme e modelli sostituiscila con le foto dei render») FOTO DEI PULSANTI: al posto
 * delle icone disegnate, le miniature dei RENDER del modello esatto (foto-ecommerce/render/pulsanti_da_render.mjs,
 * 300 x 200, ~6 KB l'una: lo stesso peso delle icone), su R2 nella cartella foto/ del servizio strade. Il modello
 * ShapeDiver non cambia: qui si sostituisce l'indirizzo dell'immagine nel JSON dell'App Builder (vale per tutti i
 * modelli CHORA gia' caricati). Selis (foglio A4): render fatto il 26 ott. Týpos usa altre foto
 * (sagoma_*): non cambia. Anche il pallino dell'oro rosa nel tono del 24 ott (vedi finituraIstantanea.ts).
 */
const FOTO_NUOVE: Record<string, string> = {
	"forma_cerchio.jpg": "render_forma_cerchio.jpg?v=4",
	"forma_quadrato.jpg": "render_forma_quadrato.jpg?v=4",
	"forma_rettangolo.jpg": "render_forma_rettangolo.jpg?v=4",
	"forma_quadrifoglio.jpg": "render_forma_quadrifoglio.jpg?v=4",
	"forma_cuore.jpg": "render_forma_cuore.jpg?v=4",
	"forma_goccia.jpg": "render_forma_goccia.jpg?v=4",
	"forma_esagono.jpg": "render_forma_esagono.jpg?v=4",
	"formaanello_cerchio.jpg": "render_anello_cerchio.jpg?v=4",
	"formaanello_quadrato.jpg": "render_anello_quadrato.jpg?v=4",
	"formaanello_rettangolo.jpg": "render_anello_rettangolo.jpg?v=4",
	"formaanello_quadrifoglio.jpg": "render_anello_quadrifoglio.jpg?v=4",
	"formaanello_cuore.jpg": "render_anello_cuore.jpg?v=4",
	"formaanello_esagono.jpg": "render_anello_esagono.jpg?v=4",
	"modello_collana.jpg": "render_modello_collana.jpg?v=4",
	"modello_bracciale.jpg": "render_modello_bracciale.jpg?v=4",
	"modello_anello.jpg": "render_modello_anello.jpg?v=4",
	"modello_orecchino-a-lobo.jpg": "render_modello_orecchino-a-lobo.jpg?v=4",
	// (29 ott 2026, Davide: «gli orecchini pendenti sono molto scuri dietro») pendente con il cerchietto e lo sfondo
	// spianato (pulsanti_da_render.mjs, opzione "piatto") -> v=6, anche per i modelli che hanno gia' la foto v=2/v=4/v=5
	"modello_orecchino-pendente.jpg": "render_modello_orecchino-pendente.jpg?v=6",
	"render_modello_orecchino-pendente.jpg?v=2": "render_modello_orecchino-pendente.jpg?v=6",
	"render_modello_orecchino-pendente.jpg?v=4": "render_modello_orecchino-pendente.jpg?v=6",
	"render_modello_orecchino-pendente.jpg?v=5": "render_modello_orecchino-pendente.jpg?v=6",
	// (26 ott 2026) Selis: render fatto; anelli schiariti (Davide: «le foto degli anelli sono molto piu' scure») -> v=3
	// (26 ott 2026, Davide: «le forme senza catena, non devono vedersi come un ciondolo») menu FORMA: solo la piastrina
	// vista dall'alto (render_forme_piastrina.py); anelli senza la banda chiara ai lati -> tutte v=4
	"forma_foglio-a4.jpg?v=20261019": "render_forma_foglio-a4.jpg?v=4",
	"anello_foglio-a4.jpg?v=20261019": "render_anello_foglio-a4.jpg?v=4",
	"metallo_oro-rosa.png?v=20261013": "metallo_oro-rosa_24ott.png",
	"metallo_oro-rosa.png": "metallo_oro-rosa_24ott.png",
};
export function aggiornaFoto(dati: unknown): void {
	const visita = (o: unknown, prof: number): void => {
		if (!o || typeof o !== "object" || prof > 40) return;
		if (Array.isArray(o)) {
			for (const x of o) visita(x, prof + 1);
			return;
		}
		const rec = o as Record<string, unknown>;
		for (const [k, v] of Object.entries(rec)) {
			if (typeof v === "string") {
				const i = v.indexOf("/foto/");
				if (k === "imageUrl" && i >= 0) {
					// la v46 del modello ha gia' le foto dei render con ?v=2: si passa alla versione nuova delle foto (v=4)
					const chiave = v.slice(i + 6);
					const nuovo = FOTO_NUOVE[chiave] ?? (/^render_.*\?v=2$/.test(chiave) ? chiave.replace(/\?v=2$/, "?v=4") : undefined);
					if (nuovo) rec[k] = v.slice(0, i + 6) + nuovo;
				}
			} else visita(v, prof + 1);
		}
	};
	visita(dati, 0);
}

/**
 * Trasforma gli "accordion" dei contenitori destro/basso in schede, una per gruppo.
 * Sul telefono restituisce una COPIA trasformata (l'originale resta a tendine: serve se la finestra si allarga);
 * sul computer l'oggetto ricevuto.
 */
export function adattaPerTelefono<T extends IAppBuilder | Error | undefined>(ricevuti: T): T {
	const telefono = schermoTelefono();
	const dati = telefono && ricevuti && !(ricevuti instanceof Error) ? copia(ricevuti) : ricevuti;
	// per le prove dal browser: window.__eidosAppBuilder = {telefono, dati}
	try {
		(window as unknown as {__eidosAppBuilder?: unknown}).__eidosAppBuilder = {telefono, dati};
	} catch {
		/* ignora */
	}
	if (!dati || dati instanceof Error) return dati;
	// (6 ott, piu' tardi) NIENTE pulsante carrello aggiunto dal fork: il modello Grasshopper lo ha gia' (AB_Carrello),
	// ma lo mostra solo quando la configurazione e' completa (segnaposto scelto): con il mio si sdoppiava.
	try {
		togliAzioniDiProva(dati);
	} catch (e) {
		console.warn("Eidos: filtro azioni non applicato", e);
	}
	try {
		aggiornaFoto(dati);
	} catch (e) {
		console.warn("Eidos: foto dei pulsanti non aggiornate", e);
	}
	if (!telefono) return dati;
	try {
		for (const c of dati.containers ?? []) {
			if (!eContenitoreStandard(c)) continue;
			const nome = String(c.name);
			if (nome !== "right" && nome !== "bottom") continue;
			if (c.tabs && c.tabs.length) continue; // gia' a schede
			const widgets = c.widgets ?? [];
			// Caso 1 (il nostro modello): un widget "accordionUi" con items = le tendine, ognuna con i suoi widget.
			// Ogni tendina diventa una scheda con gli stessi widget dentro.
			const accordionUi = widgets.filter((w) => w.type === "accordionUi");
			if (accordionUi.length) {
				const schedeUi: IAppBuilderTab[] = [];
				for (const w of accordionUi) {
					const items = (w.props as {items?: {name: string; icon?: IAppBuilderTab["icon"]; tooltip?: string; widgets: IAppBuilderWidget[]}[]}).items ?? [];
					for (const it of items) schedeUi.push({name: nomeBreve(it.name), icon: it.icon, tooltip: it.tooltip, widgets: it.widgets ?? []});
				}
				if (schedeUi.length) {
					c.tabs = schedeUi;
					c.stickyTabs = true;
					c.widgets = widgets.filter((w) => w.type !== "accordionUi");
				}
				continue;
			}
			// Caso 2 (modelli che usano il widget "accordion" classico con i gruppi dei parametri)
			const accordion = widgets.filter((w) => w.type === "accordion");
			if (!accordion.length) continue;
			const altri: IAppBuilderWidget[] = widgets.filter((w) => w.type !== "accordion");
			const schede: IAppBuilderTab[] = [];
			const perGruppo = new Map<string, IAppBuilderWidgetPropsAccordion>();
			const scheda = (gruppo: string): IAppBuilderWidgetPropsAccordion => {
				let props = perGruppo.get(gruppo);
				if (!props) {
					props = {parameters: [], exports: []};
					perGruppo.set(gruppo, props);
					schede.push({name: nomeBreve(gruppo), widgets: [{type: "accordion", props}]});
				}
				return props;
			};
			for (const w of accordion) {
				const props = w.props as IAppBuilderWidgetPropsAccordion;
				const predefinito = props.defaultGroupName ?? "Opzioni";
				for (const ref of props.parameters ?? []) {
					const gruppo = ref.overrides?.group?.name ?? predefinito;
					// dentro la scheda il parametro NON ha piu' gruppo: cosi' l'App Builder lo disegna senza tendina
					const nuovo: IAppBuilderParameterRef = {...ref, overrides: {...(ref.overrides ?? {}), group: undefined}};
					scheda(gruppo).parameters!.push(nuovo);
				}
				for (const ref of props.exports ?? []) {
					const gruppo = ref.overrides?.group?.name ?? predefinito;
					const nuovo: IAppBuilderExportRef = {...ref, overrides: {...(ref.overrides ?? {}), group: undefined}};
					scheda(gruppo).exports!.push(nuovo);
				}
			}
			if (!schede.length) continue;
			c.tabs = schede;
			c.stickyTabs = true;
			c.widgets = altri;
		}
	} catch (e) {
		console.warn("Eidos: schede del telefono non applicate", e);
	}
	return dati;
}

/**
 * (24 ott 2026, video di Davide dall'iPhone) MODALITA' SCRITTURA SUL TELEFONO: quando il cliente tocca una casella di
 * testo (indirizzo, dedica, nome) e si apre la tastiera di sistema, la casella stava nella meta' bassa dello schermo:
 * iOS faceva scorrere tutta la pagina in alto e il gioiello usciva dallo schermo. Mentre si scrive il body ha la classe
 * "eidos-scrive" (vedi totem.css): il 3D si ritira e la casella sale sopra la tastiera. Non vale sul totem (ha la sua
 * tastiera Eidos) ne' sul computer.
 */
function casellaDiTesto(t: EventTarget | null): boolean {
	if (t instanceof HTMLTextAreaElement) return !t.readOnly;
	if (!(t instanceof HTMLInputElement) || t.readOnly || t.inputMode === "none") return false;
	return ["text", "search", "email", "tel", "url", "number"].includes(t.type);
}
export function avviaScritturaTelefono(): void {
	if (typeof document === "undefined") return;
	let timer: number | undefined;
	document.addEventListener("focusin", (ev) => {
		if (!casellaDiTesto(ev.target)) return;
		if (!schermoTelefono() || document.body.classList.contains("eidos-totem")) return;
		window.clearTimeout(timer);
		document.body.classList.add("eidos-scrive"); // SUBITO: iOS misura la posizione della casella adesso
	});
	document.addEventListener("focusout", () => {
		window.clearTimeout(timer);
		// con un ritardo: il tocco su un suggerimento toglie il fuoco PRIMA della scelta (che arriva al mouseup); se la
		// pagina cambiasse subito, il dito finirebbe su un'altra riga
		timer = window.setTimeout(() => {
			if (!casellaDiTesto(document.activeElement)) document.body.classList.remove("eidos-scrive");
		}, 400);
	});
}